import React from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { IN_STORE, WAREHOUSE } from '../../util/itemSourceChannel';

import css from './ItemSourceToggle.module.css';

/**
 * Segmented "Item Source" toggle shown centered above the catalog listings on
 * the products search surfaces (`/s` and its iframe embed).
 *
 * Channel semantics are derived from `publicData.source`, using the shared rule
 * in `src/util/itemSourceChannel.js`:
 *   - 'warehouse' => source is a warehouse-backed integration (WRI, BHC)
 *   - 'in-store'  => everything else (manual + ePro), including a missing source
 *
 * The component is purely presentational; the parent owns the URL param.
 */
export { IN_STORE, WAREHOUSE };

const ItemSourceToggle = props => {
  const { className, value, onChange, label, hideLabel, fullBleedMobile } = props;
  const current = value === WAREHOUSE ? WAREHOUSE : IN_STORE;

  return (
    <div className={classNames(css.root, { [css.rootFullBleed]: fullBleedMobile }, className)}>
      {hideLabel ? null : <span className={css.label}>{label}</span>}
      <div
        className={classNames(css.toggle, { [css.toggleFullBleed]: fullBleedMobile })}
        role="group"
        aria-label={label}
      >
        <button
          type="button"
          className={classNames(css.option, {
            [css.optionFullBleed]: fullBleedMobile,
            [css.optionSelected]: current === IN_STORE,
          })}
          aria-pressed={current === IN_STORE}
          onClick={() => onChange(IN_STORE)}
        >
          In-store
        </button>
        <button
          type="button"
          className={classNames(css.option, {
            [css.optionFullBleed]: fullBleedMobile,
            [css.optionSelected]: current === WAREHOUSE,
          })}
          aria-pressed={current === WAREHOUSE}
          onClick={() => onChange(WAREHOUSE)}
        >
          Warehouse
        </button>
      </div>
    </div>
  );
};

ItemSourceToggle.defaultProps = {
  className: null,
  fullBleedMobile: false,
  hideLabel: false,
  label: 'Item Source',
  value: IN_STORE,
};

ItemSourceToggle.propTypes = {
  className: string,
  fullBleedMobile: bool,
  hideLabel: bool,
  label: string,
  value: string,
  onChange: func.isRequired,
};

export default ItemSourceToggle;
